import { type UseQueryResult, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { getPbxClient } from "@/lib/apiClient";
import { canFetchConfigOrProfile } from "@/lib/queryUtils";
import { usePathname } from "@/navigation";
import { DEFAULT_QUERY_STALE_TIME, onQueryError } from "@/queries/queryUtil";
import { isAuthenticated } from "@/store/authStore";
import { useAuthToastStore } from "@/store/authToastStore";
import type { AppConfig } from "@/types/config";

export const APP_CONFIG_QUERY_KEY = ["config"] as const;

const HELD_REFRESH_MS = 60_000;

type AuthToastStore = ReturnType<typeof useAuthToastStore>;

export async function fetchAppConfig(toastStore?: AuthToastStore): Promise<AppConfig> {
    try {
        const client = getPbxClient();
        const response = await client.get<{ ok: boolean; data: AppConfig }>("/config");
        return response.data.data;
    } catch (e) {
        if (toastStore) {
            onQueryError(e as AxiosError, toastStore);
        }
        throw e;
    }
}

/** `enabled: false` never fetches (a surface with no session, such as the public TV page); it still
 *  reads whatever the cache already holds. */
export function useAppConfigQuery(options?: { enabled?: boolean }): UseQueryResult<AppConfig> {
    const pathname = usePathname();
    const toastStore = useAuthToastStore();

    return useQuery<AppConfig, AxiosError>({
        queryKey: APP_CONFIG_QUERY_KEY,
        queryFn: () => fetchAppConfig(toastStore),
        staleTime: DEFAULT_QUERY_STALE_TIME,
        refetchOnWindowFocus: false,
        // Every useProfile() caller is an observer of this query; without this, a mount after staleTime
        // would fire a background GET /api/config. Saves still refresh it via invalidateQueries.
        refetchOnMount: false,
        retry: (failureCount, error) => {
            if ([401, 403, 404, 500].includes(error?.response?.status || 0)) {
                return false;
            }
            return failureCount < 3;
        },
        enabled: (options?.enabled ?? true) && canFetchConfigOrProfile(pathname) && isAuthenticated(),
    });
}

/** CT-756 AC 9: another user's verify must clear this tab's banner too, so re-read each minute while held. Its own
 *  query with no auth store: a passing 500 here must not sign the user out, as it does on the main config read. */
export function useRefreshConfigWhileHeld(isHeld: boolean): void {
    const queryClient = useQueryClient();

    useQuery({
        queryKey: [...APP_CONFIG_QUERY_KEY, "held-refresh"],
        queryFn: async () => {
            const config = await fetchAppConfig();
            queryClient.setQueryData(APP_CONFIG_QUERY_KEY, config);
            return config.tenant.card_unverified === true;
        },
        enabled: isHeld,
        refetchInterval: HELD_REFRESH_MS,
        retry: false,
    });
}
